import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { LauncherIcon, type IconName } from "../launcher/icons";
import { useFocusGrid } from "../useFocusGrid";
import type { VodScreenStyle } from "../vod/VodScreen";
import type { TvEmptyState } from "../../../hooks/useTvCatalogue";

/**
 * The navigation the built-in player lives inside (`DEMO-2`).
 *
 * ## Why a shell at all, and why HERE
 *
 * Until now `openTv()`'s built-in branch dropped straight into the guide. That
 * is a channel list, not a television: the vendor app a guest is used to opens
 * onto a home with a left-hand rail, and everything else - channels, films,
 * genres - hangs off it. Landing a guest in one leaf of that structure with no
 * way to reach the others reads as a broken app rather than a smaller one.
 *
 * It NESTS rather than replaces. The property's own launcher home (tiles for
 * TV, Apps, Info, Radio) is unchanged; pressing TV opens this, and Back from
 * the rail returns there. Two menus, one inside the other, is the same shape
 * the vendor app has inside the Philips launcher today - so nothing about the
 * guest's mental model changes when a premise is switched over.
 *
 * ## The rail
 *
 * Collapsed to icons, expanding to labels when it holds focus, and dimming the
 * page behind it while expanded. Copied deliberately from the app being
 * replaced: an operator comparing the two side by side should not have to
 * relearn where anything is, and "it looks like ours but the menu moved" is the
 * complaint that makes a migration feel like a downgrade.
 *
 * ## What is NOT here, and why
 *
 * - **Sök (search).** The vendor app has it; upstream's `asset/search/:query`
 *   is a STUB that always answers empty, so the app's own search box has never
 *   returned a result either. Shipping an empty box is worse than shipping no
 *   box - it is the one screen a guest can prove is broken. Building real
 *   search means building it (`TV-1`).
 * - **Inställningar (settings).** Foyer already owns settings, one level up, in
 *   a launcher every property has themed. A second settings entry inside the TV
 *   experience would be a second place to look and a second thing to keep in
 *   step.
 *
 * `Hem` (`DEMO-3`) IS here, and is the default section: a hero that follows
 * focus, the channel carousel, then the premise's own rails.
 *
 * ## Where the sections come from
 *
 * Not a hardcoded list. `Kanaler` appears when the room has a line-up and
 * `Film & Serier` when it has a library, on the same `undefined`-means-nothing
 * contract the tiles use - so a property that bought only live TV gets a rail
 * with one item rather than a menu advertising an empty shelf.
 */

export type TvSectionId = "home" | "channels" | "vod";

export interface TvSection {
  id: TvSectionId;
  /** As the rail draws it. Already in the guest's language. */
  label: string;
  icon: IconName;
  render: (p: {
    scopeRef: RefObject<HTMLElement>;
    onBack: () => void;
    /**
     * Move to another section and put the focus in it.
     *
     * The home page is a shop window onto the other sections — pressing OK on a
     * poster there has to arrive at the page that can actually play it. Without
     * this the tiles draw a focus ring, answer the arrows, and do nothing at
     * all on OK, which reads as a broken television rather than a design.
     *
     * A no-op for an id this menu does not carry, since which sections exist
     * depends on what the premise has bought.
     */
    goTo: (id: TvSectionId) => void;
  }) => React.ReactElement;
}

export interface TvShellProps {
  style: VodScreenStyle;
  /** The property's name, drawn at the head of the menu. */
  hotelName: string;
  sections: TvSection[];
  /**
   * The server's verdict on WHY there is nothing, when there is nothing.
   *
   * Shown only as a guest-facing sentence; `hint` is operator language and
   * reaches the screen only under {@link TvShellProps.diagnose}.
   */
  emptyState?: TvEmptyState;
  /**
   * Show the operator's diagnosis too. True in the CMS preview and nowhere
   * else — a guest must never read "the premise holds no content packages".
   */
  diagnose?: boolean;
  /** Leave the built-in player entirely - back to the property's own home. */
  onExit: () => void;
  /** Which section to open on arrival. Defaults to the first available. */
  initial?: TvSectionId;
}

/**
 * One character for the collapsed rail.
 *
 * A name, not a word: "Hotel Hasselbacken" should collapse to H, and a name
 * that starts with punctuation or an emoji should collapse to whatever the
 * FIRST LETTER is rather than to a bracket. Falls back to nothing rather than
 * to a placeholder glyph — an empty slot reads as a design, and a "?" reads as
 * a bug.
 */
function initialOf(name: string): string {
  const letter = Array.from(name.trim()).find((ch) => /\p{L}|\p{N}/u.test(ch));
  return letter ? letter.toLocaleUpperCase() : "";
}

const RAIL_COLLAPSED = 112;
const RAIL_EXPANDED = 380;

/**
 * The guest-facing sentence for each cause the server can report.
 *
 * Four codes, three sentences: "nothing has been chosen" and "every choice is
 * missing" are the same fact to somebody holding a remote, and differ only in
 * what an operator must go and do. Guest copy never names packages, room types
 * or catalogues — those are ours to fix, not theirs to read.
 */
function emptyLine(code: string | undefined): string {
  switch (code) {
    case "no_room_type":
    case "no_rails_selected":
    case "all_rails_dangling":
      return "Det här rummet har inget innehåll ännu.";
    case "no_rails_available":
      return "Det finns inget att visa i det här rummet just nu.";
    default:
      return "Det finns inget att visa här ännu.";
  }
}

export default function TvShell({
  style,
  hotelName,
  sections,
  emptyState,
  diagnose,
  onExit,
  initial,
}: TvShellProps) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  /**
   * Focus lives in exactly one of the two panes, and the rail's expansion is a
   * function of that rather than a third piece of state. Two booleans that must
   * agree is how a menu ends up drawn open with the focus ring somewhere else.
   */
  const [railFocused, setRailFocused] = useState(true);

  const startAt = Math.max(
    0,
    sections.findIndex((s) => s.id === (initial ?? sections[0]?.id))
  );

  // The grid's own row IS which section is shown. Moving the highlight
  // switches the page, rather than arming a choice that a second OK press
  // confirms - that is how the app being replaced behaves, and on a remote it
  // halves the presses for every navigation. Keeping `active` as separate
  // state would be a second source of truth for the same fact, and the two
  // would disagree the first time anything reset one of them.
  const [focus, setFocus] = useFocusGrid({
    rows: sections.length,
    cols: 1,
    initial: { r: startAt, c: 0 },
    scopeRef: railRef,
    enabled: railFocused,
    wrap: true,
    onEnter: () => setRailFocused(false),
    // Back out of the whole built-in player. The rail is the top of this
    // structure, so there is nowhere above it to go except the property's home.
    onBack: onExit,
  });

  const idx = Math.min(focus.r, Math.max(0, sections.length - 1));
  const section = sections[idx];
  const active = section?.id;

  /**
   * Claim the focus, and claim it AGAIN after paint.
   *
   * 🚨 The launcher template calls `useScreenFocus(scopeRef)`, which focuses
   * its own root on mount. React runs CHILD effects before parent ones, so this
   * shell focused its rail and the template immediately took it back — leaving
   * the focus on an ancestor that neither pane's scope check matches. The menu
   * drew as if it had focus, the page drew a ring on its first tile, and the
   * remote moved neither: every handler was correctly declining to act on a
   * keypress that was not theirs.
   *
   * The second claim runs after paint, which is after the parent's mount
   * effect, so it wins. Cheap, and it re-asserts on every pane change rather
   * than only at mount.
   *
   * 🚨 `sections.length` is in the deps, and that is not tidiness. The feeds
   * arrive asynchronously, so the FIRST render is almost always the empty
   * branch, where `railRef.current` is null and both claims are no-ops. When
   * the feed lands and the rail finally mounts, `railFocused` has not changed —
   * so without this dependency the effect never re-runs, focus is still sitting
   * on the scheme root (an ancestor), and every scope check correctly declines
   * every keypress. The player draws perfectly and the remote does nothing.
   *
   * The `??` chain matters for the same reason: in the empty branch the rail
   * does not exist, and focusing nothing at all is what left the Back key with
   * no element to fire on.
   */
  useEffect(() => {
    const target =
      (railFocused ? railRef.current : contentRef.current) ??
      contentRef.current ??
      railRef.current;
    target?.focus({ preventScroll: true });
    const id = requestAnimationFrame(() =>
      target?.focus({ preventScroll: true })
    );
    return () => cancelAnimationFrame(id);
  }, [railFocused, sections.length]);

  /**
   * Give the focus back on the way out.
   *
   * 🚨 This shell is the only thing under `_shared` that moves focus off the
   * scheme root. When it unmounts, the element holding focus goes with it and
   * the browser resets `document.activeElement` to `document.body` — which is
   * an ANCESTOR of the launcher root, not a descendant. `useHomeNav`'s guard
   * (`el.contains(activeElement) || activeElement === el`) therefore fails on
   * every key, and `useScreenFocus` only claims once per scheme mount and never
   * re-claims. The guest lands back on the property's home screen with a
   * completely dead remote — unable even to press the TV tile to get back in.
   *
   * So we remember whatever held focus when this shell opened (the scheme root)
   * and hand it back. It outlives us, being our ancestor, so it is always a
   * safe target.
   */
  // ⚠️ Captured during RENDER, not in an effect. The focus-claim effect above
  // is declared first and therefore runs first, so an effect here would only
  // ever record the rail this shell had already focused — and "restore" would
  // hand focus back to an element that is about to be unmounted.
  const [restoreFocusTo] = useState<HTMLElement | null>(() =>
    typeof document === "undefined"
      ? null
      : (document.activeElement as HTMLElement | null)
  );

  useEffect(() => {
    return () => {
      if (!restoreFocusTo || restoreFocusTo === document.body) return;
      if (!restoreFocusTo.isConnected) return;
      restoreFocusTo.focus?.({ preventScroll: true });
    };
  }, [restoreFocusTo]);

  /**
   * Back, bound to the window, for the empty branch.
   *
   * `useFocusGrid` owns Back for the normal case, but it is disabled when there
   * are no sections — and a React `onKeyDown` only fires when the event target
   * is that element or below it. A blank screen the remote cannot leave is not
   * acceptable even once, so this catches it at the window instead, where no
   * focus state can make it unreachable.
   */
  useEffect(() => {
    if (sections.length > 0) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Backspace" && e.key !== "Escape") return;
      e.preventDefault();
      onExit();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sections.length, onExit]);

  /**
   * Right enters the page, exactly like OK.
   *
   * The menu is a vertical list on the LEFT of the screen, so "right" is the
   * direction the page is in — a guest reaches for the arrow, not for OK, and a
   * press that does nothing there reads as the menu being stuck. `useFocusGrid`
   * cannot express this: with `cols: 1` it has nowhere to move horizontally and
   * no notion that leaving sideways means anything.
   *
   * Bound here rather than folded into the grid because it is a property of
   * this LAYOUT — a rail down the left — not of one-column grids in general.
   */
  useEffect(() => {
    if (!railFocused) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "ArrowRight") return;
      const el = railRef.current;
      if (
        !el ||
        (!el.contains(document.activeElement) && document.activeElement !== el)
      ) {
        return;
      }
      e.preventDefault();
      setRailFocused(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [railFocused]);

  if (sections.length === 0) {
    // Nothing bought, nothing to draw. The tile that opened this should not
    // have been visible either, so this is a belt-and-braces state rather than
    // a design - but a blank screen with no way out is not acceptable even
    // once, so Back still works.
    return (
      <div
        data-testid="tv-shell-empty"
        tabIndex={-1}
        ref={contentRef}
        // Back is handled by the window listener above, NOT by an `onKeyDown`
        // here. A React handler only fires when the event target is this
        // element or below it, and in this branch nothing had focus at all -
        // so the escape hatch this state exists to guarantee was unreachable.
        // Keeping both would fire `onExit` twice for one press.
        style={{
          position: "absolute",
          inset: 0,
          background: style.bg,
          color: style.inkDim,
          font: `500 28px ${style.font}`,
          display: "grid",
          placeItems: "center",
        }}
      >
        {/* 🚨 NOT "no TV is connected to this room".
            A television IS connected — it is standing here drawing this. The
            room resolved no content, which is a different fact with a
            different fix, and the old sentence sent whoever read it to check
            the pairing. `tvHomeEmptyState` already distinguishes four causes
            server-side; this is the guest-facing half of that answer. */}
        <div style={{ textAlign: "center", padding: "0 10%" }}>
          <div>{emptyLine(emptyState?.code)}</div>
          {diagnose && emptyState?.hint ? (
            // The operator's half, in a CMS preview only. It is the sentence
            // that says which of the four things to go and fix.
            <div
              data-testid="tv-shell-empty-hint"
              style={{
                marginTop: 28,
                font: `400 20px ${style.font}`,
                color: style.inkDim,
                opacity: 0.85,
                maxWidth: 900,
                lineHeight: 1.5,
              }}
            >
              {emptyState.hint}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="tv-shell"
      style={{
        position: "absolute",
        inset: 0,
        background: style.bg,
        display: "flex",
        overflow: "hidden",
      }}
    >
      <div
        ref={railRef}
        tabIndex={-1}
        data-testid="tv-rail"
        data-expanded={railFocused ? "true" : "false"}
        style={{
          width: railFocused ? RAIL_EXPANDED : RAIL_COLLAPSED,
          flex: `0 0 ${railFocused ? RAIL_EXPANDED : RAIL_COLLAPSED}px`,
          height: "100%",
          background: style.card,
          borderRight: `1px solid ${style.rule}`,
          display: "flex",
          flexDirection: "column",
          // The property's name sits at the TOP, not floated with the menu.
          // It is a masthead, not a menu item: centring it with the sections
          // made the two read as one list whose first row happened to be
          // bigger.
          justifyContent: "flex-start",
          // Nothing may escape the rail. Collapsed it is 112px wide and the
          // property's name is not; without this the name runs out across the
          // page behind it, which is the one thing a menu must never do.
          overflow: "hidden",
          gap: 8,
          // The rail sits ABOVE the content while expanded, rather than
          // pushing it: a layout that reflows on focus makes every rail below
          // jump sideways as the guest moves down the menu.
          position: "relative",
          zIndex: 2,
          transition: "width 160ms ease",
          outline: "none",
        }}
      >
        {/*
          The property's name, at the head of its own menu.

          The vendor app puts an operator mark here and it is the only thing on
          screen that says whose television this is — the full-bleed player has
          no template header above it to carry that. Collapsed, the rail is
          112px and a hotel name is not, so it degrades to an initial rather
          than being clipped mid-word or, worse, spilling across the page.
        */}
        <div
          data-testid="tv-rail-name"
          title={hotelName}
          style={{
            height: 96,
            flex: "0 0 96px",
            marginTop: 48,
            display: "flex",
            alignItems: "center",
            justifyContent: railFocused ? "flex-start" : "center",
            padding: `0 ${railFocused ? 32 : 0}px`,
            marginBottom: 16,
            color: style.ink,
            font: `700 ${railFocused ? 30 : 34}px ${style.titleFont}`,
            // Belt and braces with the rail's own `overflow: hidden`: this is
            // the element that would do the spilling.
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0,
          }}
        >
          {railFocused ? hotelName : initialOf(hotelName)}
        </div>
        {/* The menu itself takes the rest, and centres within it. */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {sections.map((s, i) => {
            const on = s.id === active;
            return (
              <div
                key={s.id}
                data-section={s.id}
                data-testid={`tv-rail-${s.id}`}
                data-focused={railFocused && i === idx ? "true" : undefined}
                aria-current={on ? "true" : undefined}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  height: 88,
                  padding: `0 ${railFocused ? 32 : 0}px`,
                  justifyContent: railFocused ? "flex-start" : "center",
                  color: on ? style.accent : style.inkDim,
                  // The selected item carries a bar on the leading edge, not a
                  // filled row: a full-width fill at 88px tall reads as a button
                  // on a television at three metres, and the guest starts
                  // pressing OK on the menu instead of moving through it.
                  boxShadow: on ? `inset 4px 0 0 0 ${style.accent}` : undefined,
                  background:
                    railFocused && i === idx
                      ? `${style.accent}1f`
                      : "transparent",
                  font: `600 30px ${style.font}`,
                  whiteSpace: "nowrap",
                }}
              >
                <LauncherIcon name={s.icon} size={40} />
                {railFocused && <span>{s.label}</span>}
              </div>
            );
          })}
        </div>
      </div>

      <div
        ref={contentRef}
        tabIndex={-1}
        data-testid="tv-content"
        style={{
          position: "relative",
          flex: 1,
          height: "100%",
          minWidth: 0,
          // Dimmed rather than blurred while the menu is open. A blur on a
          // 1080p panel is a full-screen filter every frame, and the decode
          // budget these templates run on is already the thing that stalls
          // (`panel-video-encoding`).
          opacity: railFocused ? 0.45 : 1,
          transition: "opacity 160ms ease",
          outline: "none",
        }}
      >
        {section?.render({
          scopeRef: contentRef,
          // Back inside a section returns to the menu, never straight out.
          // Leaving the whole player is a second, deliberate press - a guest
          // who overshoots one Back should land on the menu, not on the
          // property's home screen wondering what happened.
          onBack: () => setRailFocused(true),
          goTo: (id: TvSectionId) => {
            const to = sections.findIndex((s) => s.id === id);
            if (to < 0) return;
            setFocus({ r: to, c: 0 });
            setRailFocused(false);
          },
        })}
      </div>
    </div>
  );
}
